var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Checklist_1;
import React, { Component, Fragment } from 'react';
import { Density, Direction } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, eventListener, oneOf, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { getPrettyListValue } from '@philips-internal/oswald-ui-model';
import { classNames, enumValues, generateUUID, isDefined, isEnumValid, isFunction, isMobile, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { Checkbox } from '../checkbox/checkbox.js';
import { Loader } from '../loader/loader.js';
import { NativeSelect } from '../native-select/native-select.js';
import { ChecklistAPI } from './checklist-api.js';
import './checklist.css';
const checklistItemShape = shape({
    key: string,
    label: string,
    strikethrough: bool,
    value: bool,
});
let Checklist = Checklist_1 = class Checklist extends Component {
    constructor() {
        super(...arguments);
        this.onChange = (item, checked) => {
            const { multiSelect, value: oldValue, onChange } = this.props;
            const isAlreadyChecked = oldValue.some(({ key }) => key === item.key);
            if (checked && isAlreadyChecked)
                return;
            let value = checked ? [item] : [];
            if (multiSelect) {
                value = checked
                    ? [...oldValue, item]
                    : oldValue.filter(({ key }) => key !== item.key);
            }
            if (isFunction(onChange)) {
                onChange(new ChangeEvent({
                    oldValue,
                    prettyValue: Checklist_1.formatPrettyValue({ value }),
                    value,
                }));
            }
        };
        this.onChangeNative = ({ value }) => {
            const { value: oldValue, onChange } = this.props;
            const arrayValue = value ? [value] : [];
            if (isFunction(onChange)) {
                onChange(new ChangeEvent({
                    oldValue,
                    prettyValue: Checklist_1.formatPrettyValue({ value: arrayValue }),
                    value: arrayValue,
                }));
            }
        };
        this.onChangeCheckAll = (event) => {
            const { value: oldValue, data = Checklist_1.defaultProps.data, onChange, } = this.props;
            const arrayValue = event.value ? data : [];
            if (isFunction(onChange)) {
                onChange(new ChangeEvent({
                    oldValue,
                    prettyValue: Checklist_1.formatPrettyValue({ value: arrayValue }),
                    value: arrayValue,
                }));
            }
        };
        this.isChecked = ({ key }) => {
            const { value } = this.props;
            return Array.isArray(value) && value.some(item => item.key === key);
        };
    }
    /**
     * Prettifies a given value.
     *
     * @param {object} properties - Properties to generate pretty value.
     * @param {string | number} [properties.value] - Value to be formatted.
     * @returns {string} pretty value.
     */
    static formatPrettyValue(props) {
        return getPrettyListValue(props.value);
    }
    shouldComponentUpdate(nextProps) {
        return shallowCompare(this.props, nextProps, [
            'className',
            'data',
            'density',
            'disabledKeys',
            'enabled',
            'loading',
            'name',
            'nativeOnMobile',
            'readOnly',
            'showCheckAll',
            'strikethrough',
            'uuid',
            'value',
        ]);
    }
    isAllChecked() {
        const { data = Checklist_1.defaultProps.data } = this.props;
        return data.every(this.isChecked);
    }
    renderCheckAll() {
        const { enabled, multiSelect, readOnly, showCheckAll } = this.props;
        if (readOnly || !showCheckAll || !multiSelect) {
            return null;
        }
        return (React.createElement("li", { className: "c-checklist__item c-checklist__check-all" },
            React.createElement(Checkbox, { enabled: enabled, label: "tws.checkbox.check-all", name: generateUUID(), onChange: this.onChangeCheckAll, readOnly: readOnly, uuid: "checklist-checkbox-all-uuid", value: this.isAllChecked() })));
    }
    renderItems() {
        const { data, disabledKeys = Checklist_1.defaultProps.disabledKeys, enabled, name = '', readOnly, strikethrough, } = this.props;
        if (!Array.isArray(data)) {
            return null;
        }
        const validData = data.filter((item) => isDefined(item.key));
        if (!validData.length) {
            return React.createElement("li", { className: "c-checklist__item", "data-testid": "checklist-no-options" }, MessageService.translate('tws.checkbox.no-options'));
        }
        return (React.createElement(Fragment, null,
            validData.map((item) => {
                const checkboxName = `${name}-${item.key}`;
                const itemEnabled = !disabledKeys.includes(item.key);
                return (React.createElement("li", { className: "c-checklist__item", "data-testid": "checklist-option", key: item.key },
                    React.createElement(Checkbox, { enabled: enabled && itemEnabled, label: MessageService.translate(item.label), name: checkboxName, onChange: (event) => this.onChange(item, event.value), readOnly: readOnly, strikethrough: Boolean(strikethrough) || Boolean(item.strikethrough), uuid: `checklist-checkbox-item-${item.key}-uuid`, value: this.isChecked(item) })));
            }),
            this.renderCheckAll()));
    }
    renderChecklist() {
        const { className, density, direction, loading } = this.props;
        const clClassName = classNames({
            'c-checklist': true,
            [`c-checklist--${direction}`]: isEnumValid(Direction, direction),
            [`c-checklist--${density}`]: isEnumValid(Density, density),
            [`${className}`]: Boolean(className),
        });
        return (React.createElement("div", { className: clClassName },
            React.createElement("ul", { className: "c-checklist__items" }, loading
                ? React.createElement("li", null,
                    React.createElement(Loader, null))
                : this.renderItems())));
    }
    renderNative() {
        const { data = Checklist_1.defaultProps.data, value } = this.props;
        return React.createElement(NativeSelect, { data: data, onChange: this.onChangeNative, value: value[0] });
    }
    render() {
        const isNative = isMobile() && this.props.nativeOnMobile;
        return isNative
            ? this.renderNative()
            : this.renderChecklist();
    }
};
Checklist.propTypes = {
    className: string,
    data: arrayOf(checklistItemShape).notEnumerable(),
    density: oneOf(enumValues(Density)),
    direction: oneOf(enumValues(Direction)),
    disabledKeys: arrayOf(string).notEnumerable(),
    enabled: bool,
    loading: bool,
    multiSelect: bool.group('behaviour'),
    name: string,
    nativeOnMobile: bool,
    onChange: eventListener,
    readOnly: bool,
    showCheckAll: bool,
    strikethrough: bool,
    uuid: string,
    value: arrayOf(checklistItemShape).notEnumerable(),
};
Checklist.defaultProps = {
    data: [],
    density: Density.REGULAR,
    direction: Direction.VERTICAL,
    disabledKeys: [],
    enabled: true,
    loading: false,
    multiSelect: true,
    nativeOnMobile: false,
    showCheckAll: true,
    value: [],
};
Checklist = Checklist_1 = __decorate([
    AwesomeComponent({ api: ChecklistAPI, type: ComponentType.DATA_ENTRY })
], Checklist);
export { Checklist, };
//# sourceMappingURL=checklist.js.map